import React from "react";
import { Box, Stack, Typography, Tooltip } from "@mui/material";

/**
 * Seat occupancy as a filled track against a capacity end-cap.
 *
 * A bare percentage hides the headroom, so the numbers are always rendered beside the bar and
 * colour is never the only carrier of meaning.
 *
 * Capacity is `course_plans.max_seats`; `filled` is the enrolled count resolved server-side by
 * CoursePlanSeatMapper. When a group has no capacity set there is nothing to fill against, so the
 * bar is replaced by the headcount alone rather than a misleading full or empty track.
 */

const BANDS = [
  { upTo: 0.8, track: "#dcfce7", fill: "#16a34a", note: "Filling" },
  { upTo: 1,   track: "#ffedd5", fill: "#ea580c", note: "Nearly full" },
];
const OVER = { track: "#fee2e2", fill: "#dc2626", note: "At or over capacity" };

export default function SeatBar({ filled, capacity, compact = false }) {
  const enrolled = Number(filled) || 0;

  if (capacity == null || Number(capacity) <= 0) {
    return (
      <Tooltip title="No capacity set for this group" arrow placement="top">
        <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700 }}>
          {enrolled} <Box component="span" sx={{ color: "#9aa1ac", fontWeight: 600 }}>· no limit set</Box>
        </Typography>
      </Tooltip>
    );
  }

  const total = Number(capacity);
  const ratio = enrolled / total;
  const band = BANDS.find((b) => ratio < b.upTo) || OVER;
  const width = `${Math.min(100, Math.max(0, ratio * 100))}%`;

  return (
    <Tooltip title={`${band.note} — ${enrolled} of ${total} seats taken`} arrow placement="top">
      <Stack spacing={0.5} sx={{ minWidth: compact ? 90 : 120 }}>
        <Typography variant="body2" sx={{ color: "#111827", fontWeight: 700, lineHeight: 1.2 }}>
          {enrolled}
          <Box component="span" sx={{ color: "#6b7280", fontWeight: 600 }}> / {total}</Box>
        </Typography>
        <Box
          sx={{
            position: "relative",
            height: 6,
            borderRadius: 999,
            bgcolor: band.track,
            overflow: "hidden",
          }}
        >
          <Box sx={{ height: "100%", width, bgcolor: band.fill, borderRadius: 999 }} />
        </Box>
      </Stack>
    </Tooltip>
  );
}
